import {
  collection,
  doc,
  getDocs,
  limit as qLimit,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type DocumentData,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "@/firebaseConfig";
import type { Module, ActionContext } from "vuex";
import { Organisation, UserData } from "@/models/interfaces";

/** One of the organisations a user belongs to, with their level in it. */
export interface Membership {
  account: number;
  usertype: number;
  title: string;
}

export interface OrganisationsState {
  current: Organisation | null;
  memberships: Membership[];
  /** Every organisation on the platform, by reference: masters only. */
  all: Organisation[];
  fetchedAccountId: number | null;
  fetching: boolean;
  error: string | null;
}

type RootState = unknown;

const state = (): OrganisationsState => ({
  current: null,
  memberships: [],
  all: [],
  fetchedAccountId: null,
  fetching: false,
  error: null,
});

const mutations = {
  SET_CURRENT(s: OrganisationsState, organisation: Organisation | null) {
    s.current = organisation;
  },
  SET_MEMBERSHIPS(s: OrganisationsState, memberships: Membership[]) {
    s.memberships = memberships;
  },
  SET_ALL(s: OrganisationsState, all: Organisation[]) {
    s.all = all;
  },
  PATCH_ORGANISATION(s: OrganisationsState, { id, patch }: { id: string; patch: Partial<Organisation> }) {
    s.all = s.all.map((o) => (o.id === id ? { ...o, ...patch } : o));
    if (s.current?.id === id) s.current = { ...s.current, ...patch };
  },
  SET_ICON(s: OrganisationsState, icon: string) {
    if (s.current) s.current.icon = icon;
  },
  SET_FETCHED_ACCOUNT_ID(s: OrganisationsState, id: number | null) {
    s.fetchedAccountId = id;
  },
  SET_FETCHING(s: OrganisationsState, v: boolean) {
    s.fetching = v;
  },
  SET_ERROR(s: OrganisationsState, msg: string | null) {
    s.error = msg;
  },
};

const actions = {
  /**
   * Loads the organisation matching the given account id. A user's `account`
   * is the organisation's `reference` (see auth/register), which older
   * documents may store as a string, so match on both like the
   * organisationReferenceExists callable does.
   */
  async fetchByAccountId(
    { state, commit }: ActionContext<OrganisationsState, RootState>,
    accountId: number | null,
  ) {
    if (accountId == null) {
      commit("SET_CURRENT", null);
      commit("SET_FETCHED_ACCOUNT_ID", null);
      return;
    }
    if (state.fetching || state.fetchedAccountId === accountId) return;

    commit("SET_FETCHING", true);
    commit("SET_ERROR", null);
    try {
      const q = query(
        collection(db, "organisations"),
        where("reference", "in", [accountId, String(accountId)]),
        qLimit(1),
      );
      const snap = await getDocs(q);
      const found = snap.docs[0];

      if (found) {
        const data = found.data() as DocumentData;
        commit("SET_CURRENT", {
          id: found.id,
          reference: data.reference as number | string,
          title: (data.title as string) ?? "",
          icon: (data.icon as string) ?? "",
        });
      } else {
        commit("SET_CURRENT", null);
      }
      commit("SET_FETCHED_ACCOUNT_ID", accountId);
    } catch (err: any) {
      commit("SET_ERROR", err?.message ?? String(err));
    } finally {
      commit("SET_FETCHING", false);
    }
  },

  /**
   * The organisations the user belongs to, by title: their `organisations`
   * map, or only `account` on documents from before it. The rules let them
   * read every organisation in their map.
   */
  async fetchMemberships(
    { commit }: ActionContext<OrganisationsState, RootState>,
    user: UserData | null,
  ) {
    const levels = membershipLevels(user);
    const accounts = Object.keys(levels).map(Number);
    if (!accounts.length) {
      commit("SET_MEMBERSHIPS", []);
      return;
    }
    const titles = new Map<number, string>();
    // `in` takes at most 30 values, two per organisation.
    for (let i = 0; i < accounts.length; i += 15) {
      const chunk = accounts.slice(i, i + 15);
      const snap = await getDocs(
        query(
          collection(db, "organisations"),
          where("reference", "in", chunk.flatMap((a) => [a, String(a)])),
        ),
      );
      for (const d of snap.docs) {
        titles.set(Number(d.data().reference), String(d.data().title ?? ""));
      }
    }
    commit(
      "SET_MEMBERSHIPS",
      accounts
        .map((account) => ({
          account,
          usertype: levels[String(account)],
          title: titles.get(account) ?? "",
        }))
        .sort((a, b) => (a.title || String(a.account)).localeCompare(b.title || String(b.account))),
    );
  },

  /**
   * Makes `account` the user's active organisation: one of theirs, at the
   * level they have there, or, if it is another organisation's reference,
   * joins it at level 0, as signing up with that reference does. The one
   * left is written into their map first if it was missing, so they can
   * switch back. A master is master in every organisation, so they take
   * level 5 wherever they switch to, the one they are in included. The
   * rules allow exactly this (`switches()`). Everything in the app is loaded
   * for the active organisation, so the page reloads.
   * Throws "unknown-reference" when no organisation has that reference.
   */
  async switchTo(
    _: ActionContext<OrganisationsState, RootState>,
    { user, account }: { user: UserData; account: number },
  ) {
    const uid = user.id ?? user.uid;
    if (!uid || !Number.isInteger(account) || account <= 0) return;
    const master = isMaster(user);
    if (Number(user.account) === account && (!master || Number(user.usertype) >= 5)) return;

    const organisations = { ...(user.organisations ?? {}) };
    const current = Number(user.account ?? 0);
    if (current > 0 && !(String(current) in organisations)) {
      organisations[String(current)] = Number(user.usertype ?? 0);
    }
    if (!(String(account) in organisations)) {
      const exists = await httpsCallable<{ reference: string }, { exists: boolean }>(
        functions,
        "organisationReferenceExists",
      )({ reference: String(account) });
      if (!exists.data.exists) throw new Error("unknown-reference");
      organisations[String(account)] = 0;
    }
    if (master) organisations[String(account)] = 5;

    await updateDoc(doc(db, "users", uid), {
      account,
      usertype: Number(organisations[String(account)] ?? 0),
      organisations,
    });
    window.location.assign("/");
  },

  /** Every organisation on the platform, by reference. Masters only. */
  async fetchAll({ commit }: ActionContext<OrganisationsState, RootState>) {
    const snap = await getDocs(collection(db, "organisations"));
    commit(
      "SET_ALL",
      snap.docs
        .map((d) => {
          const data = d.data() as DocumentData;
          return {
            id: d.id,
            reference: data.reference as number | string,
            title: String(data.title ?? ""),
            icon: String(data.icon ?? ""),
          };
        })
        .sort((a, b) => Number(a.reference) - Number(b.reference)),
    );
  },

  /**
   * Creates an organisation, as a master: the document's id is its
   * reference, so the rules refuse a second one with the same reference;
   * older organisations have other ids, so those are looked for first.
   * Throws "reference-taken" when one already has it.
   */
  async create(
    { commit, state }: ActionContext<OrganisationsState, RootState>,
    { reference, title }: { reference: number; title: string },
  ) {
    const taken = await getDocs(
      query(collection(db, "organisations"), where("reference", "in", [reference, String(reference)]), qLimit(1)),
    );
    if (!taken.empty) throw new Error("reference-taken");
    const ref = doc(db, "organisations", String(reference));
    await setDoc(ref, {
      reference,
      title: title.trim(),
      icon: "",
      createdAt: serverTimestamp(),
      createdBy: auth.currentUser?.uid ?? "",
    });
    commit(
      "SET_ALL",
      [...state.all, { id: ref.id, reference, title: title.trim(), icon: "" }].sort(
        (a, b) => Number(a.reference) - Number(b.reference),
      ),
    );
  },

  /** Renames any organisation, as a master. */
  async rename(
    { commit }: ActionContext<OrganisationsState, RootState>,
    { id, title }: { id: string; title: string },
  ) {
    await updateDoc(doc(db, "organisations", id), { title: title.trim(), updatedAt: serverTimestamp() });
    commit("PATCH_ORGANISATION", { id, patch: { title: title.trim() } });
  },

  /** Sets any organisation's icon, as a master. */
  async updateIconOf(
    { commit }: ActionContext<OrganisationsState, RootState>,
    { id, iconURL }: { id: string; iconURL: string },
  ) {
    await updateDoc(doc(db, "organisations", id), { icon: iconURL, updatedAt: serverTimestamp() });
    commit("PATCH_ORGANISATION", { id, patch: { icon: iconURL } });
  },

  async updateIcon(
    { state, commit }: ActionContext<OrganisationsState, RootState>,
    payload: { iconURL: string; callback?: () => void },
  ) {
    if (!state.current) throw new Error("No organisation loaded");
    await updateDoc(doc(db, "organisations", state.current.id), {
      icon: payload.iconURL,
      updatedAt: serverTimestamp(),
    });
    commit("SET_ICON", payload.iconURL);
    payload?.callback?.();
  },
};

const getters = {
  organisation(state: OrganisationsState): Organisation | null {
    return state.current;
  },
  memberships(state: OrganisationsState): Membership[] {
    return state.memberships;
  },
  all(state: OrganisationsState): Organisation[] {
    return state.all;
  },
  title(state: OrganisationsState): string {
    return state.current?.title ?? "";
  },
  icon(state: OrganisationsState): string {
    return state.current?.icon ?? "";
  },
  isFetching(state: OrganisationsState): boolean {
    return state.fetching;
  },
  error(state: OrganisationsState): string | null {
    return state.error;
  },
};

/**
 * Whether the user is one of the platform's owners: a master (`usertype` 5)
 * in the organisation they are active in or in any other of theirs, as the
 * rules' `isMaster()` decides.
 */
export function isMaster(user: UserData | null): boolean {
  if (!user) return false;
  return (
    Number(user.usertype) >= 5 ||
    Object.values(user.organisations ?? {}).some((level) => Number(level) >= 5)
  );
}

/** The user's level in each of their organisations, by reference. */
function membershipLevels(user: UserData | null): Record<string, number> {
  if (!user) return {};
  const levels: Record<string, number> = {};
  for (const [key, level] of Object.entries(user.organisations ?? {})) {
    if (Number(key) > 0) levels[key] = Number(level);
  }
  if (Number(user.account) > 0) levels[String(Number(user.account))] = Number(user.usertype ?? 0);
  return levels;
}

export const organisations: Module<OrganisationsState, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
export default organisations;
